<script module lang="ts">
  import type { OrganizationDetail } from "@kontextwerk/control-web-client"
  import type { PermissionState } from "@kontextwerk/control-web-frame/logic/permissions/model"
  import type { PropertiesViewFrameContext } from "./context"
</script>

<script lang="ts">
  import { onDestroy, onMount } from "svelte"
  import { OrgRootBrandingMount, frameConfig } from "@kontextwerk/control-web-frame"
  import {
    ActivityContextPanel,
    composeControlActivityCatalog,
  } from "@kontextwerk/control-web-frame"
  import PageTemplate from "@kontextwerk/web-sdk/ui/layout/PageTemplate.svelte"
  import type { RecordDetailConfig } from "@kontextwerk/web-sdk/ui/layout/recordDetail/types"
  import { buildPropertyHeader, propertyRecordIcon } from "./_logic/header"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { location, spaNavigate } from "@kontextwerk/web-sdk/core"
  import { getAdminApi } from "../../../../../../../../api/kehrblatt/client"
  import { fetchCustomers } from "../../../../../../../../api/kehrblatt/customer-registry/customers"
  import {
    fetchPropertyDetailProjection,
    updateProperty,
  } from "../../../../../../../../api/kehrblatt/property-registry/properties"
  import { fetchPropertyCustomers } from "../../../../../../../../api/kehrblatt/property-customer-registry/property-customers"
  import { fetchOrganization } from "../../../../../../../../api/kehrblatt/organizations"
  import {
    registerEventCallback,
    unregisterEventCallback,
  } from "../../../../../../../_shared/_logic/eventbus"
  import { ApiError } from "@kontextwerk/control-web-frame/api/client/errors"
  import { updateAssistContext } from "../../../../../../_shared/_logic/assist-index"
  import { buildAppPermissionState } from "../../../../../../../_shared/_logic/permissions-model"
  import GeneralPage from "./pages/General"
  import UnitsPage from "./pages/Units"
  import PropertyArchiveActions from "./_modules/property-archive-actions"
  import { buildPropertyDetailViewNavigation, buildPropertyDetailViewPaths } from "./navigation"
  import { getKehrblattControlAuthContext } from "../../../../../../../_shared/_logic/auth-context"
  import {
    isFreeTrialExperienceOrganization,
    isFreeTrialRestrictionActive,
  } from "../../../../../../_shared/_logic/free-trial-experience"
  import { archivePresentation } from "../../../../../../_shared/_logic/archive-presentation"

  interface Props {
    organizationId: string
    organizationSlug?: string | null
    organizationResolvedId?: string | null
    propertyId: string
  }

  let {
    organizationId,
    organizationSlug = null,
    organizationResolvedId = null,
    propertyId,
  }: Props = $props()
  const authContext = getKehrblattControlAuthContext()
  const emptyPermissions = buildAppPermissionState(null)
  const activityCatalog = $derived(composeControlActivityCatalog($frameConfig?.activity?.catalog))
  const uuidPattern =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
  const isUuid = (value: string | null | undefined) => Boolean(value && uuidPattern.test(value))

  let property = $state<Property | null>(null)
  let resolvedOrganization = $state<OrganizationDetail | null>(null)
  const apiOrganizationId = $derived(
    resolvedOrganization?.id ??
      organizationResolvedId ??
      (isUuid(organizationId) ? organizationId : null),
  )
  let propertyLinks = $state<PropertyCustomer[]>([])
  let customers = $state<Customer[]>([])
  let loading = $state(true)
  let error = $state<string | null>(null)
  let saving = $state(false)
  let importRefreshTimeout: ReturnType<typeof setTimeout> | null = null

  const normalizePath = (value: string | null | undefined) => {
    const raw = (value ?? "").replace(/\/+$/, "") || "/"

    return raw || "/"
  }

  const decodeSegment = (value: string | null | undefined) => {
    if (typeof value !== "string") return null

    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  const pathOrgSegment = $derived.by(() => {
    const segments = normalizedPath.split("/").filter(Boolean)

    if (segments[0] === "organizations" && segments[1]) {
      return decodeSegment(segments[1])
    }

    return null
  })

  const organizationIdentifier = $derived(
    organizationResolvedId ?? organizationId ?? organizationSlug ?? pathOrgSegment ?? null,
  )
  const effectiveOrganizationId = $derived(apiOrganizationId ?? "")
  const organizationPathSegment = $derived(
    resolvedOrganization?.slug ?? organizationSlug ?? pathOrgSegment ?? organizationId ?? null,
  )
  const brandingRouteScopeKey = $derived(
    organizationPathSegment ? `organization:${organizationPathSegment}` : null,
  )
  const administrationPath = $derived(
    `/organizations/${encodeURIComponent(organizationPathSegment ?? organizationId)}/administration`,
  )
  const propertiesListPath = $derived(`${administrationPath}/properties`)

  const basePath = $derived(
    organizationPathSegment && propertyId
      ? `/organizations/${encodeURIComponent(organizationPathSegment)}/addresses/${encodeURIComponent(propertyId)}`
      : "",
  )
  const tabPaths = $derived(buildPropertyDetailViewPaths(basePath))
  const normalizedPath = $derived(normalizePath($location.path))

  $effect(() => {
    if (!basePath || !tabPaths.general) return

    if (normalizedPath === basePath) {
      spaNavigate(tabPaths.general, { replace: true, noSearchParam: false })
    }

    if (normalizedPath === tabPaths.contactRouting) {
      spaNavigate(tabPaths.units || tabPaths.general, { replace: true, noSearchParam: false })
    }
  })

  const permissions = $derived($authContext?.permissions ?? emptyPermissions)
  const isFreeTrialExperience = $derived(isFreeTrialExperienceOrganization(resolvedOrganization))
  const trialRestrictionsActive = $derived(
    isFreeTrialRestrictionActive(resolvedOrganization, permissions.isSuper),
  )
  const canManageArchive = $derived(
    !trialRestrictionsActive && permissions.allows("termin.scheduling", "manage"),
  )
  const confirmedProperty = $derived(
    property?.id === propertyId && !error && property?.organization_id === effectiveOrganizationId
      ? property
      : null,
  )
  // The record frame stands from the route on; the loaded property only fills the header.
  const recordDetail = $derived<RecordDetailConfig>({
    key: `property:${organizationId}:${propertyId}`,
    loading: loading && !confirmedProperty,
    header: confirmedProperty
      ? buildPropertyHeader(confirmedProperty)
      : { icon: propertyRecordIcon, fields: [] },
  })
  const canEditCustomers = $derived(!trialRestrictionsActive && !property?.archived_at)
  const propertySubtitle = $derived.by(() => {
    if (!property?.archived_at) return property?.city || "Adressdetails"

    return `${property.city || "Adressdetails"} · ${archivePresentation(property.archived_at, property.archive_reason)}`
  })
  const viewFrameContext = $derived.by<PropertiesViewFrameContext>(() => ({
    permissions,
    organization: resolvedOrganization,
    property,
    organizationId: effectiveOrganizationId,
    propertyLinks,
    customers,
    canEditCustomers,
    isFreeTrialExperience,
    trialRestrictionsActive,
    loading,
    error,
    saving,
    onRetry: load,
    onSubmit: handleUpdate,
    onChanged: handleLinksChanged,
  }))

  const pageNavigation = $derived.by(() =>
    buildPropertyDetailViewNavigation({
      context: viewFrameContext,
      paths: tabPaths,
      snippets: {
        general: generalPageContent,
        units: unitsPageContent,
      },
    }),
  )

  const load = async () => {
    const orgIdentifier = organizationIdentifier

    if (!orgIdentifier || !propertyId) return

    loading = true
    error = null
    try {
      const organization = await fetchOrganization(orgIdentifier)
      resolvedOrganization = organization
      const detailPayload = await fetchPropertyDetailProjection(propertyId)
      property = detailPayload.property ?? null
      propertyLinks = detailPayload.property_customers ?? []
      customers = detailPayload.customers ?? []

      if (!property) {
        error = "Liegenschaft konnte nicht gefunden werden."
      }
    } catch (err) {
      console.error("property_load_failed", err)
      error = "Liegenschaft konnte nicht geladen werden."
    } finally {
      loading = false
    }
  }

  onMount(load)

  const scheduleImportRefresh = () => {
    if (importRefreshTimeout) return

    importRefreshTimeout = setTimeout(() => {
      importRefreshTimeout = null
      void load()
    }, 400)
  }

  onMount(() => {
    const handlerId = `property-import-${propertyId}`
    const handler = () => scheduleImportRefresh()
    registerEventCallback("assist.import_progress", handlerId, handler)

    return () => unregisterEventCallback("assist.import_progress", handlerId)
  })

  onDestroy(() => {
    if (importRefreshTimeout) {
      clearTimeout(importRefreshTimeout)
      importRefreshTimeout = null
    }
  })

  const handleUpdate = async (payload: PropertyUpdatePayload) => {
    if (!property || !effectiveOrganizationId || !canEditCustomers) return

    saving = true
    try {
      const updated = await updateProperty(property.id, payload)
      property = updated
      try {
        const refreshedLinks = await fetchPropertyCustomers({
          propertyId: updated.id,
          organizationId: effectiveOrganizationId,
        })
        propertyLinks = refreshedLinks ?? propertyLinks
      } catch (err) {
        console.warn("property_links_refresh_failed", err)
      }
      newNotification({ class: "success", html: "Liegenschaft aktualisiert." })
    } catch (err) {
      console.error("property_update_failed", err)
      const message =
        err instanceof ApiError && err.message
          ? err.message
          : "Liegenschaft konnte nicht gespeichert werden."
      newNotification({ class: "error", html: message })
    } finally {
      saving = false
    }
  }

  const handleLinksChanged = (payload: { property: Property; links: PropertyCustomer[] }) => {
    property = payload.property
    propertyLinks = payload.links
    const customerIds = Array.from(
      new Set(payload.links.map((entry) => entry.customer_id).filter(Boolean)),
    )

    if (!customerIds.length) {
      customers = []

      return
    }

    if (!effectiveOrganizationId) return

    void fetchCustomers({
      organizationId: effectiveOrganizationId,
      customerIds,
      limit: customerIds.length,
      archiveScope: "ALL",
    })
      .then((response) => {
        customers = response?.items ?? []
      })
      .catch((err) => {
        console.error("property_linked_customers_refresh_failed", err)
      })
  }

  $effect(() => {
    if (!property || !effectiveOrganizationId) return

    updateAssistContext({
      page: "PROPERTY_DETAIL",
      organizationId: effectiveOrganizationId,
      propertyId: property.id,
    })
  })
</script>

<OrgRootBrandingMount
  organizationId={effectiveOrganizationId}
  routeScopeKey={brandingRouteScopeKey}
  source="kehrblatt-property-detail"
/>

{#snippet generalPageContent(activeTab: string)}
  <GeneralPage {viewFrameContext} {activeTab} />
{/snippet}

{#snippet unitsPageContent(activeTab: string)}
  <UnitsPage {viewFrameContext} {activeTab} />
{/snippet}

{#if confirmedProperty}
  <ActivityContextPanel
    context={{
      organization_id: confirmedProperty.organization_id,
      object_type: "property",
      object_id: confirmedProperty.id,
    }}
    label={confirmedProperty.street
      ? `${confirmedProperty.street} ${confirmedProperty.house_number ?? ""}`.trim()
      : "Liegenschaft"}
    operations={getAdminApi().activity}
    catalog={activityCatalog}
  />
{/if}
<PropertyArchiveActions
  property={confirmedProperty}
  disabled={!canManageArchive || saving}
  onChanged={(updated) => (property = updated)}
>
  {#snippet children(actions)}
    <PageTemplate
      viewKey={`property:${organizationId}:${propertyId}`}
      title={property?.street
        ? `${property.street} ${property.house_number ?? ""}`.trim()
        : "Liegenschaft"}
      subtitle={propertySubtitle}
      {pageNavigation}
      appBreadcrumbs={[
        { title: "Verwaltungsbereich", href: administrationPath },
        { title: "Liegenschaften", href: propertiesListPath },
        {
          title: property?.street
            ? `${property.street} ${property.house_number ?? ""}`.trim()
            : "Liegenschaft",
        },
      ]}
      locationStore={location}
      gobackLink={propertiesListPath}
      recordDetail={{ ...recordDetail, actions }}
    />
  {/snippet}
</PropertyArchiveActions>
